import { DataSection } from "components/molecules/Card/CardTypes";
import { ReactNode } from "react";
import { getDisplayInfo } from "utils/formatting";
import { percentageDifference } from "utils/mathFormulas/mathFormulas.helper";

const prepareCarbonPenaltiesCardData = (
    referencePlan: CarbonReductionPlan,
    comparedPlan: CarbonReductionPlan,
    referencePlanName: string,
    comparedPlanName: string
) => {
    // Carbon Policy Savings
    const referenceCarbonPenaltiesSavings = referencePlan.planLifetimeCarbonPolicySavings || 0;
    const comparedCarbonPenaltiesSavings = comparedPlan.planLifetimeCarbonPolicySavings || 0;
    const carbonPenaltiesSavingsEvolution = percentageDifference(
        comparedCarbonPenaltiesSavings,
        referenceCarbonPenaltiesSavings
    );

    // Chart Data
    const chartCarbonPenaltiesData = transformToCarbonPenaltiesChartData(
        referencePlan.annualSummaries,
        comparedPlan.annualSummaries
    );

    // Card Data
    const { cardHeaderData } = createCardData(referenceCarbonPenaltiesSavings, carbonPenaltiesSavingsEvolution);

    // Tooltip
    const { unit: refPenaltiesUnit, formatFunction: carbonPenaltiesFormatFunction } = getDisplayInfo(
        "planLifetimeCarbonCostsAvoided",
        referenceCarbonPenaltiesSavings
    );
    const { unit: compPenaltiesUnit } = getDisplayInfo(
        "planLifetimeCarbonCostsAvoided",
        comparedCarbonPenaltiesSavings
    );
    const formatedCarbonPoliciesSavingRef = carbonPenaltiesFormatFunction(referenceCarbonPenaltiesSavings);
    const formatedCarbonPoliciesSavingCompared = carbonPenaltiesFormatFunction(comparedCarbonPenaltiesSavings);

    const tooltipElement: ReactNode = (
        <>
            {comparedPlanName} carbon policies savings are {formatedCarbonPoliciesSavingCompared} {compPenaltiesUnit}
            <br />
            <br />
            {referencePlanName} carbon policies savings are {formatedCarbonPoliciesSavingRef} {refPenaltiesUnit}
            <br />
            <br />
            There is a {carbonPenaltiesSavingsEvolution.toFixed(0)}% difference between the two plans
        </>
    );
    return {
        cardHeaderData,
        chartCarbonPenaltiesData,
        tooltipElement,
    };
};

const createCardData = (referenceCarbonPenaltiesSavings: number, carbonPenaltiesSavingsEvolution: number) => {
    const cardHeaderData: DataSection = [
        {
            value: referenceCarbonPenaltiesSavings,
            displayInfoKey: "planLifetimeCarbonCostsAvoided",
            title: "Net",
            valueColor: "neutral",
            increaseDecreasePercentage: carbonPenaltiesSavingsEvolution,
        },
    ];

    return {
        cardHeaderData,
    };
};

const transformToCarbonPenaltiesChartData = (
    referenceAnnualSummary: AnnualSummary[],
    comparedAnnualSummary: AnnualSummary[]
) =>
    referenceAnnualSummary
        .map((referenceYear) => {
            const matchingComparedYear = comparedAnnualSummary.find(
                (comparedYear) => comparedYear.year === referenceYear.year
            );

            return {
                avoidedPenaltiesReference: referenceYear.carbonPolicySavings,
                avoidedPenaltiesCompared: matchingComparedYear?.carbonPolicySavings,
                year: referenceYear.year,
            };
        })
        .sort((a, b) => a.year - b.year);

export default prepareCarbonPenaltiesCardData;
